import type { AssistantActionStatus, AssistantObjectRef, AssistantPendingDTO, AssistantUndoableDTO } from './assistant';
import type { MissionPauseReason, MissionStatus } from './assistant-missions';
import type { OrderOffReason } from './assistant-orders';
import type { Id, ISODate } from './common';
import { DELEGATED_S9_RULES } from './delegation';
import type { Permission } from './rbac';

// Proactive Hana (S3): rules, briefing sections, nudge and memory enums, the projection record, and the settings DTOs.
// Every enum below is also a CHECK in 023; `tests/unit/proactive-contracts.test.ts` reads the migration and compares.

/** The rules of §7.1: switchable by the company (`assistant_rule_settings`) and by each person
 *  (`assistant_proactive_prefs.disabled_rules`). The ten S3 rules (023's CHECKs), then S9's three delegated rules
 *  (030 restates the CHECKs with them, §4.3). */
export const PROACTIVE_RULES = ['work.overdue_pile', 'work.blocked_long', 'work.stale', 'people.overloaded',
  'project.idle', 'project.ending', 'finance.over_budget', 'finance.overdue_entry', 'strategy.metric_off',
  'delegated.silent', ...DELEGATED_S9_RULES] as const;
export type ProactiveRule = (typeof PROACTIVE_RULES)[number];
/** What a signal may carry: the rules plus the person's own reminders and watches (§7.6), which never switch off
 *  by rule. */
export const SIGNAL_RULES = [...PROACTIVE_RULES, 'user.reminder', 'user.watch'] as const;
export type SignalRule = (typeof SIGNAL_RULES)[number];

/** The rule names a person reads (mockup §4, L1 D25). */
export const RULE_LABELS: Record<ProactiveRule, string> = {
  'work.overdue_pile': 'Việc quá hạn dồn',
  'work.blocked_long': 'Việc bị chặn lâu',
  'work.stale': 'Việc đang làm đứng yên',
  'people.overloaded': 'Người đang quá tải',
  'project.idle': 'Dự án đứng im',
  'project.ending': 'Dự án sắp kết thúc',
  'finance.over_budget': 'Dự án vượt ngân sách',
  'finance.overdue_entry': 'Khoản thu chi quá hạn',
  'strategy.metric_off': 'Mục tiêu lệch hướng',
  'delegated.silent': 'Việc đã giao im lặng',
  'delegated.due_soon': 'Việc đã giao sắp đến hạn',
  'delegated.overdue': 'Việc đã giao quá hạn',
  'delegated.blocked': 'Việc đã giao bị chặn',
};

/** The sections a briefing may hold (§8.2); the order is the order on the card and on the settings page. S6 §10 adds
 *  `external` (the person's outside connections, daily only), last. */
export const BRIEFING_SECTIONS = ['overdue', 'today', 'delegated', 'changes', 'nudges', 'reminders', 'projects',
  'finance', 'strategy', 'week', 'external'] as const;
export type BriefingSection = (typeof BRIEFING_SECTIONS)[number];
export const BRIEFING_SECTION_LABELS: Record<BriefingSection, string> = {
  overdue: 'Quá hạn',
  today: 'Hạn hôm nay',
  delegated: 'Việc đã giao và chờ nhận',
  changes: 'Thay đổi',
  nudges: 'Lời nhắc',
  reminders: 'Lịch nhắc',
  projects: 'Dự án',
  finance: 'Tài chính',
  strategy: 'Chiến lược',
  week: 'Tuần',
  external: 'Hôm nay ngoài hệ thống',
};
export const BRIEFING_KINDS = ['daily', 'weekly'] as const;
export type BriefingKind = (typeof BRIEFING_KINDS)[number];

export const NUDGE_STATES = ['pending', 'shown', 'acted', 'dismissed', 'snoozed', 'resolved', 'deferred'] as const;
export type NudgeState = (typeof NUDGE_STATES)[number];
export const SIGNAL_SEVERITIES = ['low', 'normal', 'high'] as const;
export type SignalSeverity = (typeof SIGNAL_SEVERITIES)[number];
/** Whether the model wrote a text or the rule's template did (§6.4). */
export const PROACTIVE_TEXT_SOURCES = ['llm', 'template'] as const;
export type ProactiveTextSource = (typeof PROACTIVE_TEXT_SOURCES)[number];

export const MEMORY_SCOPES = ['personal', 'company'] as const;
export type MemoryScope = (typeof MEMORY_SCOPES)[number];
export const MEMORY_KINDS = ['preference', 'habit', 'fact', 'glossary'] as const;
export type MemoryKind = (typeof MEMORY_KINDS)[number];
export const MEMORY_SOURCES = ['told', 'learned', 'admin', 'suggested'] as const;
export type MemorySource = (typeof MEMORY_SOURCES)[number];
export const MEMORY_STATUSES = ['active', 'proposed', 'rejected'] as const;
export type MemoryStatus = (typeof MEMORY_STATUSES)[number];

// The projection record (§4). Server-side only: it is stored in `contributions` and never reaches a DTO, a log, MCP
// or the audit (R5).

/** What a contribution can come from. Beyond the chip types it names finance entries, metrics, activity and audit rows,
 *  memories and the person's own reminders (`watch`, L3 D113), whose checkers L2, L3 and L4 register; a kind with no
 *  checker counts as not visible (fail closed). `contributions` has no kind CHECK, so `watch` needs no migration.
 *  `external` (S6 §5.2, §10): an item of the briefing's outside snapshot, its id the connection's. */
export const PROACTIVE_SOURCE_KINDS = ['task', 'note', 'project', 'objective', 'priority', 'user', 'finance_entry',
  'metric', 'activity', 'audit', 'memory', 'watch', 'external'] as const;
export type ProactiveSourceKind = (typeof PROACTIVE_SOURCE_KINDS)[number];
export type ProactiveSourceRef = { type: ProactiveSourceKind; id: Id };
/** A snapshot value: a number, an ISO date, an enum code or a flag. Never a title or a name — stored. The one exception
 *  is transient: `user.watch`'s read-time `admit` adds a deleted subject's `name` the reader may still see (D68 `{tên}`),
 *  which is never written to a row. */
export type ProactiveValue = number | string | boolean | null;
/** `{section, sourceKind, sourceId, objectRefs, requiredPermissions, values, sourceKeys}` (§4). `section` is the fact
 *  group it feeds (a briefing section, or the rule's own group for a nudge). `objectRefs` are the parents and related
 *  objects that must stay visible too. `sourceKeys` are audience uuids, or '*' for a source with no safe audience. */
export type ProactiveContribution = { section: string; sourceKind: ProactiveSourceKind; sourceId: Id;
  objectRefs: ProactiveSourceRef[]; requiredPermissions: Permission[]; values: Record<string, ProactiveValue>;
  sourceKeys: string[] };

// Time fields (§12.2): the system has no time picker, so every time is a Select on a 15-minute grid.

export const QUIET_GRID_MINUTES = 15;
const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const minutesOf = (time: string): number => {
  const match = HHMM.exec(time);
  if (match === null) throw new Error(`not an HH:MM time: ${time}`);
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  if (minutes % QUIET_GRID_MINUTES !== 0) throw new Error(`off the ${QUIET_GRID_MINUTES}-minute grid: ${time}`);
  return minutes;
};
/** Every grid stop from `from` to `to`, both included, as `HH:MM`. */
export function timeOptions(from: string, to: string): string[] {
  const start = minutesOf(from);
  const end = minutesOf(to);
  if (end < start) throw new Error(`timeOptions: ${to} is before ${from}`);
  const out: string[] = [];
  for (let m = start; m <= end; m += QUIET_GRID_MINUTES) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }
  return out;
}
/** The briefing time window (§12.2, D10). */
export const BRIEFING_TIME_MIN = '05:00';
export const BRIEFING_TIME_MAX = '11:00';
export const DAILY_CAP_MAX = 20;
export const PROACTIVE_DAILY_BUDGET_MAX = 1_000_000_000;

// The rule table (§7.1, §12.1, L2 D47, D48, D70).

/** One integer parameter of a rule: its key in `params`, its default and the inclusive range an admin may set. `unit`
 *  is the word shown after the input on the "Luật nhắc" card. Every rule `days` counts workdays (L2 D46). */
export type RuleParamSpec = { key: string; default: number; min: number; max: number; unit: string };
const DAYS = 'ngày';
/** L2 D47 ★: the parameters each rule takes, in the order the card shows them. */
export const RULE_PARAM_SPECS = {
  'work.overdue_pile': [{ key: 'min', default: 3, min: 2, max: 20, unit: 'việc' }],
  'work.blocked_long': [{ key: 'days', default: 3, min: 1, max: 30, unit: DAYS }],
  'work.stale': [{ key: 'days', default: 7, min: 2, max: 60, unit: DAYS }],
  'people.overloaded': [{ key: 'overdue', default: 5, min: 2, max: 50, unit: 'việc' },
    { key: 'factor', default: 2, min: 2, max: 5, unit: '× trung vị' }],
  'project.idle': [{ key: 'days', default: 14, min: 3, max: 90, unit: DAYS }],
  'project.ending': [{ key: 'days', default: 7, min: 1, max: 30, unit: DAYS }],
  'finance.over_budget': [],
  'finance.overdue_entry': [{ key: 'days', default: 3, min: 1, max: 60, unit: DAYS }],
  'strategy.metric_off': [{ key: 'half', default: 50, min: 10, max: 90, unit: '% quý' }],
  // S9 §6.1: the four delegated rules take no company parameter; the cadence lives on each card (`work_delegations`).
  'delegated.silent': [],
  'delegated.due_soon': [],
  'delegated.overdue': [],
  'delegated.blocked': [],
} as const satisfies Record<ProactiveRule, readonly RuleParamSpec[]>;
type ParamsOf<S extends readonly RuleParamSpec[]> =
  S extends readonly [] ? Record<string, never> : { [K in S[number]['key']]: number };
/** The `params` a rule reads, keyed by exactly its spec keys. */
export type ProactiveRuleParams = { [R in ProactiveRule]: ParamsOf<(typeof RULE_PARAM_SPECS)[R]> };

/**
 * A rule's parameters as stored (`assistant_rule_settings.params`, any JSON) made safe to use: each spec key that is
 * missing, not an integer or outside its range takes its default, and every other key is dropped (L2 D47). A missing
 * settings row is `undefined` here and reads as all defaults.
 */
export function ruleParamsOrDefault<R extends ProactiveRule>(rule: R, stored: unknown): ProactiveRuleParams[R] {
  const source = typeof stored === 'object' && stored !== null && !Array.isArray(stored)
    ? stored as Record<string, unknown> : {};
  const out: Record<string, number> = {};
  for (const spec of RULE_PARAM_SPECS[rule] as readonly RuleParamSpec[]) {
    const value = source[spec.key];
    out[spec.key] = typeof value === 'number' && Number.isInteger(value) && value >= spec.min && value <= spec.max
      ? value : spec.default;
  }
  return out as ProactiveRuleParams[R];
}

/** L2 D48 ★: the one sentence under each rule's name on the card. */
export const RULE_DESCRIPTIONS: Record<ProactiveRule, string> = {
  'work.overdue_pile': 'Nhắc khi một người có nhiều việc quá hạn',
  'work.blocked_long': 'Việc bị chặn quá số ngày làm việc',
  'work.stale': 'Việc đang làm không có cập nhật quá số ngày làm việc',
  'people.overloaded': 'Nhắc người giao khi một người có quá nhiều việc',
  'project.idle': 'Không có hoạt động trên dự án',
  'project.ending': 'Dự án sắp tới ngày kết thúc mà còn việc mở',
  'finance.over_budget': 'Chi thực tế cộng dự kiến vượt ngân sách chi của dự án',
  'finance.overdue_entry': 'Khoản thu chi quá hạn quá số ngày làm việc',
  'strategy.metric_off': 'Chỉ số không đạt hoặc quá nửa quý chưa có đánh giá',
  'delegated.silent': 'Người nhận chưa báo tiến độ quá số ngày làm việc đặt trên thẻ',
  'delegated.due_soon': 'Còn trong số ngày làm việc đặt trên thẻ trước hạn mà chưa xong',
  'delegated.overdue': 'Đã qua hạn mà người nhận chưa báo xong',
  'delegated.blocked': 'Bị chặn quá số ngày làm việc đặt trên thẻ',
};

/** Company counts of the rule's nudges over the last 30 days, by the nudge's own timestamps (L2 D70). No content. */
export type ProactiveRuleStatsDTO = { generated: number; shown: number; acted: number; dismissed: number };
/** One row of "Luật nhắc": a missing settings row reads as enabled with the defaults. */
export type ProactiveRuleRowDTO = { rule: ProactiveRule; enabled: boolean; params: Record<string, number>;
  stats30d: ProactiveRuleStatsDTO };
/** What the admin PUT writes for one rule; out-of-range params are a 422, not a silent default. */
export type ProactiveRuleSettingInput = { rule: ProactiveRule; enabled: boolean; params: Record<string, number> };

// The admin tab "Chủ động" (§12.1, D9, D19).

/** Today's proactive tokens for the company (D19). `estimatedTokens` is what abandoned calls were charged with no
 *  usage from the model. `admissionClosed`: a model reported more than its bound, so model calls stop for the day. */
export type ProactiveUsageTodayDTO = { day: string; budget: number; accountedTokens: number; reservedTokens: number;
  estimatedTokens: number; admissionClosed: boolean };
/** `version` is `assistant_settings.proactive_version` (D9), 0 when never saved. Times are `HH:MM`.
 *  `templateRate` is template ÷ all composed in 30 days, null when nothing was composed. */
export type ProactiveSettingsDTO = { proactiveEnabled: boolean; nudgesEnabled: boolean; briefingsEnabled: boolean;
  memoryEnabled: boolean; dailyBudget: number; modelId: Id | null; quietStart: string; quietEnd: string;
  quietWeekend: boolean; dailyCap: number; version: number; usageToday: ProactiveUsageTodayDTO;
  templateRate: number | null;
  /** Company briefings composed in the last 30 days, and how many of those were seen (L3 D130). No content. */
  briefingStats30d: BriefingStats30dDTO;
  /** The ten rules in `PROACTIVE_RULES` order, with their 30-day stats (L2 D70). */
  rules: ProactiveRuleRowDTO[] };
/** `rules` absent leaves every rule as it is, so a client that does not know the table never resets it (L2 D70). */
export type ProactiveSettingsBody = { version: number; proactiveEnabled: boolean; nudgesEnabled: boolean;
  briefingsEnabled: boolean; memoryEnabled: boolean; dailyBudget: number; modelId: Id | null; quietStart: string;
  quietEnd: string; quietWeekend: boolean; dailyCap: number; rules?: ProactiveRuleSettingInput[] };

// The personal page "Hana của tôi" (§12.2, D9, D23).

/** Own quiet hours; null = "Theo công ty". */
export type ProactiveQuiet = { start: string; end: string; weekend: boolean };
/** What the company decides, shown beside the person's own choice. */
export type ProactiveCompanyDefaults = { proactiveEnabled: boolean; nudgesEnabled: boolean; briefingsEnabled: boolean;
  memoryEnabled: boolean; quiet: ProactiveQuiet; dailyCap: number };
/** `version` 0 = no row yet. `memoryEnabled`/`memoryLearning` are the person's own memory switches ("Dùng trí nhớ",
 *  "Cho Hana tự học"); the memory page writes them through the same PUT (L4 D166). */
export type ProactivePrefsDTO = { nudgesEnabled: boolean; briefingDaily: boolean; briefingWeekly: boolean;
  briefingTime: string; briefingSections: BriefingSection[]; quiet: ProactiveQuiet | null; dailyCap: number | null;
  disabledRules: ProactiveRule[]; memoryEnabled: boolean; memoryLearning: boolean; version: number;
  company: ProactiveCompanyDefaults; rules: { rule: ProactiveRule; enabledByCompany: boolean }[] };
/** Nudge and briefing fields (D23), plus the memory switches (L4 D166): absent = unchanged, so "Hana của tôi", which
 *  does not show them, never resets them. A change of either bumps the memory fence in the same transaction. */
export type ProactivePrefsBody = { version: number; nudgesEnabled: boolean; briefingDaily: boolean;
  briefingWeekly: boolean; briefingTime: string; briefingSections: BriefingSection[]; quiet: ProactiveQuiet | null;
  dailyCap: number | null; disabledRules: ProactiveRule[]; memoryEnabled?: boolean; memoryLearning?: boolean };

/** D130: `composed_at` inside the last 30 days; `seen` = those with `seen_at` set. */
export type BriefingStats30dDTO = { composed: number; seen: number };

// Briefings (§8, L3 D114, D121–D127).

/** One stored fact row (D114): the source it counts and its numbers, dates and codes — never a title, which is
 *  resolved at read time with the reader's fresh principal. `ref` null = a row with no object. */
export type BriefingFactItem = { ref: ProactiveSourceRef | null; values: Record<string, ProactiveValue> };
/**
 * One section of the facts (D114). `count` counts every visible contribution, `items` the top 10 by the section's
 * order key, `totals` sums over the visible contributions (keys per section: `delegated` {blocked, late}, `nudges`
 * {high}, `finance` weekly {incomeActual, incomePlanned, expenseActual, expensePlanned}, `week` {done,
 * delegatedDone, next}). An empty section stays with count 0; one the reader may not see is absent.
 */
export type BriefingSectionFacts = { section: BriefingSection; count: number; items: BriefingFactItem[];
  totals: Record<string, number> };
/** `since`: the instant the `changes` window opens (null when the briefing has no such window). Sections in §8.2 order. */
export type BriefingFacts = { kind: BriefingKind; period: string; since: string | null;
  sections: BriefingSectionFacts[] };

export const BRIEFING_TONES = ['late', 'warn', 'ok', 'info'] as const;
export type BriefingTone = (typeof BRIEFING_TONES)[number];
/** The small Hana beside the card (§8.4). */
export type BriefingEmotion = 'worried' | 'proud' | 'greet';
/** One card row: a chip (`ref`, its `label` resolved now with the reader's rights) or a total with no chip
 *  (`ref: null`), with its server-rendered hint and tone (D125). */
export type BriefingItemDTO = { ref: AssistantObjectRef | null; label: string; hint: string; tone: BriefingTone;
  /** S6 §10: an `external` row (`ref` null, `label` the item's plain title) — what the snapshot says of it now. */
  external?: BriefingExternalDTO };
/** An outside item's details (S6 §10): `at` business-local (a date, or `YYYY-MM-DDTHH:MM+07:00`), `url` `https:`
 *  only, `server` the server's current name, `serverId` its id (the tile's colour, as Kết nối and Workplace). */
export type BriefingExternalDTO = { kind: 'event' | 'message' | 'task'; at?: string; from?: string; url?: string;
  server: string; serverId: Id };
/** One group on the card: `items` are at most the top 10; `more` = `count` − the rows the DTO carries. */
export type BriefingGroupDTO = { section: BriefingSection; heading: string; count: number; items: BriefingItemDTO[];
  more: number };
/**
 * A briefing as the card, the history detail and the bubble read it, always projected for the reader (§4, D115).
 * `text` is the composed text, or the template over the projected facts when anything changed (`textSource`
 * 'template'). `current`: the row of the current period (today's daily, this week's weekly inside its window) — only
 * that one can be recomposed; `recomposeLeft` counts today's "Soạn lại" presses left (0..3). `alternate` (the weekly
 * card only): the latest daily, projected, or null (D124).
 */
export type BriefingDTO = { id: Id; kind: BriefingKind; period: string; title: string; when: string; text: string;
  textSource: ProactiveTextSource; emotion: BriefingEmotion; groups: BriefingGroupDTO[]; collapsedLine: string;
  seenAt: string | null; composedAt: string; current: boolean; recomposeLeft: number;
  /** S9 L4 D135: the template is waiting for the worker's model text (app-open or "Soạn lại" ran out of time): the open
   *  card reads it again until this goes false. */
  textPending: boolean; alternate?: BriefingDTO | null };
/** `GET /api/assistant/briefings?limit=30` (D127): metadata only — no facts, no counts. */
export type BriefingListItemDTO = { id: Id; kind: BriefingKind; period: string; composedAt: string;
  seenAt: string | null; title: string };
/** `GET /api/assistant/briefings/current` (D124): null when there is nothing to show. */
export type BriefingCurrentDTO = { briefing: BriefingDTO | null };

/** `POST /api/assistant/app-open` body (D122). An old client's `{}` means `greet: false`. */
export type AppOpenBody = { greet?: boolean };
/** Hana's opening bubble (§8.5, D123): the template over the projected facts, never the model's text; `strong` lists
 *  the substrings of `text` to bold. */
export type AppOpenBubbleDTO = { kind: 'briefing'; briefingId: Id; text: string; strong: string[] };
/** `POST /api/assistant/app-open` (D22, L3 D122). */
export type AppOpenDTO = { bubble: AppOpenBubbleDTO | null };

// Nudges (§7.3–§7.5, L2 D50, D57, D59, D60).

/** What a nudge may point at: the chip types of a signal subject (023 `assistant_signals.subject_type` minus `watch`). */
export const PROACTIVE_SUBJECT_TYPES = ['task', 'note', 'project', 'objective', 'priority', 'user'] as const;
export type ProactiveSubjectType = (typeof PROACTIVE_SUBJECT_TYPES)[number];
export type ProactiveSubjectRef = { type: ProactiveSubjectType; id: Id };

/**
 * One action a rule offers (§7.5), written by code, never by a model. `build` names an input builder registered on the
 * server (`NUDGE_BUILDS`); `params` are its fixed arguments (`{ days: 7 }`, `forceConfirm`). Server-side only: it is
 * stored in `assistant_nudges.actions` and reaches the browser as a `NudgeActionView`.
 */
export type NudgeActionTemplate =
  | { kind: 'capability'; capability: string; build: string; params: Record<string, unknown>; label: string }
  | { kind: 'open'; target: ProactiveSubjectRef | { route: string }; label: string }
  | { kind: 'ask'; label: string };
export type NudgeActionKind = NudgeActionTemplate['kind'];
/** An action chip as the browser sees it: no builder, no params. `index` is its place in the nudge's `actions`, the
 *  `:index` of `POST /nudges/:id/actions/:index`. `open` carries exactly one of `target` and `route`. `forceConfirm`:
 *  a capability whose card opens with its Sửa form already open (§7.5, D94), the one param the tray needs. */
export type NudgeActionView = { index: number; kind: NudgeActionKind; label: string;
  target?: ProactiveSubjectRef; route?: string; forceConfirm?: true;
  /** S9 D48, D62: on a `work.nudge` action only — the note the press would send (preview = what is sent). */
  note?: string;
  /** S9 D48, §6.5: on a `work.nudge` action only — today's contacts from the reader to its recipient (nudges plus
   *  progress asks, HCM day), against `limit` = `NUDGE_PAIR_LIMIT`; `name` is the recipient's given name (D31). */
  contact?: NudgeContactView };
export type NudgeContactView = { used: number; limit: number; name: string };

/** One shown nudge in the tray. `text` is the composed text, or the template text when the facts changed since.
 *  `reminder`: a `user.reminder`, whose chip is "Xong" (`POST /nudges/:id/done`). */
export type NudgeDTO = { id: Id; rule: SignalRule; severity: SignalSeverity; text: string;
  actions: NudgeActionView[]; reminder: boolean; shownAt: string };
/** A receipt's live chain under its nudge's row (§7.5): a pending card, or an undo card still in its window. It survives
 *  the nudge being acted or resolved and never counts towards the badge. */
export type NudgeActionCardDTO = { nudgeId: Id; index: number; pending?: AssistantPendingDTO;
  undoable?: AssistantUndoableDTO };
/**
 * S7 §9.3 (T16): one of the reader's own background runs in the tray, not dismissed — needing its owner (`attention`,
 * whatever its age: D136), or waiting or finished within 3 days with work Hoàn tác can still take back; or resuming
 * after its owner answered (`queued`/`running`, D97). `owner.title` is the
 * mission's title or the order's name now; `at` when it finished, else when it started. `objects`: what its executed
 * writes touched, as chips. `undo`: its writes still undoable (`until` = the earliest end, when the run's undo closes),
 * named by `groupSummary`; null when none is. `pending`: its cards still open (the run's endpoints act on them), each
 * due 3 days after it was made. `asked`: its mission's open question is this run's. `errorCode`: why it failed.
 */
export type RunCardDTO = { runId: Id; owner: { kind: 'mission' | 'order'; id: Id; title: string }; at: string;
  status: 'queued' | 'running' | 'waiting' | 'done' | 'failed' | 'cancelled'; summary: string | null;
  objects: AssistantObjectRef[]; undo: { count: number; until: string; summary: string } | null;
  pending: AssistantPendingDTO[]; asked: boolean; errorCode: string | null;
  /** D107: the run token cap of the run's mission or order, and the mission's token budget (null for an order). */
  runTokenCap: number; tokenBudget: number | null;
  /** D136: the run's mission's status and pause reason now (`stale`: its goal card is gone); null for an order. */
  missionStatus: MissionStatus | null; pauseReason: MissionPauseReason | null;
  /** L4 P5: the run is its order's newest and the order went off by itself — why ("Lệnh đã tắt vì …"); else null. */
  orderOff: OrderOffReason | null };
/** `GET /api/assistant/nudges` (L2 D59). Output off → `enabled: false` with empty lists. `badgeCount` = `items.length`.
 *  S7 §9.3: `runCards` are built before that branch, so they come with nudges off too, newest first, at most 10; they
 *  never count towards the badge. */
export type NudgeTrayDTO = { enabled: boolean; items: NudgeDTO[]; actionCards: NudgeActionCardDTO[];
  badgeCount: number; runCards: RunCardDTO[] };
/** `POST /nudges/:id/actions/:index`: the client keeps the same `requestId` on every retry of one click. */
export type NudgeActionBody = { requestId: Id };
/**
 * Its answer (L2 T8.1): the button's chain as it stands — `actionId` is the end of its edit chain, `status` that row's,
 * with the card the tray would show for it (`pending` while it waits, `undoable` while Hoàn tác is open). A retry
 * with the same key answers this again, whatever happened to the chain since.
 */
export type NudgeActionResultDTO = NudgeActionCardDTO & { actionId: Id; status: AssistantActionStatus };
/** "Để sau" (§7.3): one hour; "Chiều nay" 13:30, only before 12:00; "Sáng mai" 07:30 of the next workday. */
export const NUDGE_SNOOZE_UNTIL = ['1h', 'afternoon', 'tomorrow'] as const;
export type NudgeSnoozeUntil = (typeof NUDGE_SNOOZE_UNTIL)[number];
export type NudgeSnoozeBody = { until: NudgeSnoozeUntil };
/** `offerDisableRule`: this was the third dismissal of the rule in 14 days, so the tray offers "Tắt nhắc này" (D60). */
export type NudgeDismissDTO = { offerDisableRule: boolean };

// Reminders and watches (§7.6, §10.1, §11, L2 D65, D66).

/** Active reminders and watches a person may have at once (§5.1). */
export const REMINDER_LIMIT = 50;
export const WATCH_LIMIT = 30;
export const RECURRENCE_FREQS = ['daily', 'weekly', 'monthly'] as const;
export type RecurrenceFreq = (typeof RECURRENCE_FREQS)[number];
export const RECURRENCE_INTERVAL_MAX = 12;
/**
 * A reminder's repeat (023 `recurrence`). Every occurrence is counted from the anchor, never from the last fire.
 * `interval` 1..12. `byWeekday` (weekly only) uses ISO days, 1 = Monday … 7 = Sunday; absent = the anchor's weekday.
 * `workdaysOnly` (daily only) skips Saturday and Sunday. `until` is the last local day that may fire, included.
 */
export type Recurrence = { freq: RecurrenceFreq; interval: number; byWeekday?: number[]; workdaysOnly?: boolean;
  until?: ISODate };
/** A subject as a schedule shows it: `label` is resolved with the reader's own rights; a subject the reader no longer
 *  sees is `null` on the row (hidden = missing). */
export type WatchSubjectDTO = { type: ProactiveSubjectType; id: Id; label: string };
/**
 * One of the person's own reminders or watches. `nextAt` (reminder) is the next fire instant; `anchorLocal` the
 * reminder's local anchor `YYYY-MM-DDTHH:MM` at Asia/Ho_Chi_Minh, which an edit of the note keeps and an edit of the
 * time or recurrence replaces. A watch has neither.
 */
export type WatchDTO = { id: Id; kind: 'reminder' | 'watch'; note: string | null; subject: WatchSubjectDTO | null;
  nextAt: string | null; anchorLocal: string | null; recurrence: Recurrence | null; active: boolean; version: number;
  createdAt: string };
/** `POST /api/assistant/watches`. `at` is an ISO instant with an offset, in the future and at most 366 days away. A
 *  watch's subject is a task (with its subtree) or a project. */
export type WatchCreateBody =
  | { kind: 'reminder'; at: string; recurrence?: Recurrence | null; note: string; subject?: ProactiveSubjectRef | null }
  | { kind: 'watch'; subject: { type: 'task' | 'project'; id: Id } };
/** `PATCH /api/assistant/watches/:id` (reminders): `at`/`recurrence` set a new anchor; `note` alone keeps it. */
export type WatchPatchBody = { version: number; at?: string; recurrence?: Recurrence | null; note?: string };
